import type { ReceivingFlowState, ReceivingStep } from './flow'
import { planPalletContents, receivingPrinters } from './labels'
import { measuredProfile } from './measurements'
import type { InventoryControlCategory } from './fixtures'
import { fixtureReceiptDate } from './fixtures'
import { inventoryControlFor, inventoryControls } from './inventoryControl'

export interface ReceiptAttributes {
  lot: string
  expiry: string
  gtin: string
  manufactureDate?: string
  bestBeforeDate?: string
  receivedDate?: string
}
export interface ContainerReceipt {
  lpn: string
  received: number
  status: 'in-progress' | 'received' | 'closed-short' | 'voided'
  parentLpn?: string
  location: string
  directQuantity: number
  directLot?: string
  attributes?: ReceiptAttributes
}
export interface ReceiptEvent {
  id: string
  kind: 'pallet' | 'child' | 'loose'
  lpn: string
  parentLpn: string
  quantity: number
  location: string
  attributes?: ReceiptAttributes
}
export interface PendingReceipt {
  kind: ReceiptEvent['kind']
  lpn: string
  parentLpn: string
  quantity: number
  returnStep: ReceivingStep
}
export interface ReceiptProgress {
  scan: string
  quantity: string
  activePallet: string | null
  containers: Record<string, ContainerReceipt>
  events: ReceiptEvent[]
  pending: PendingReceipt | null
  attributes: ReceiptAttributes
  replacementSubmitted: boolean
  replacementRequests: {
    lpn: string
    printerId: string
    status: 'accepted' | 'rejected'
    confirmed: boolean
    declined?: boolean
    sku: string
    quantity: number
    attributes: ReceiptAttributes
  }[]
}
export type ReceiptAction =
  | { type: 'set-receipt-scan'; value: string }
  | { type: 'scan-receiving-pallet' }
  | { type: 'scan-receiving-child' }
  | { type: 'open-loose-receipt' }
  | { type: 'set-loose-quantity'; value: string }
  | { type: 'submit-loose-receipt' }
  | { type: 'switch-receiving-pallet' }
  | { type: 'finish-receiving-pallet' }
  | { type: 'done-pallet-work' }
  | { type: 'set-controlled-attribute'; field: keyof ReceiptAttributes; value: string }
  | { type: 'submit-controlled-attributes'; captureOnly?: boolean }
  | { type: 'cancel-pending-receipt' }
  | { type: 'print-container-replacement' }
  | { type: 'confirm-container-replacement' }
  | { type: 'decline-container-replacement' }
  | { type: 'receipt-back' }
  | { type: 'set-fixture-inventory-category'; category: InventoryControlCategory }

const blankAttributes = (): ReceiptAttributes => ({
  lot: '',
  expiry: '',
  gtin: '',
  manufactureDate: '',
  bestBeforeDate: '',
  receivedDate: '',
})
export const createReceiptProgress = (): ReceiptProgress => ({
  scan: '',
  quantity: '',
  activePallet: null,
  containers: {},
  events: [],
  pending: null,
  attributes: blankAttributes(),
  replacementSubmitted: false,
  replacementRequests: [],
})
export const receiptProgress = (state: ReceivingFlowState) => state.receipt ?? createReceiptProgress()
export const receiptLineKey = (state: ReceivingFlowState) => `${state.selectedAsn?.id}:${state.activeVariant?.sku}`
export const receiptRemaining = (state: ReceivingFlowState) =>
  Math.max(
    (state.activeVariant?.expectedUnits ?? 0) +
      (state.additionalByLine?.[receiptLineKey(state)] ?? 0) -
      (state.activeVariant?.receivedUnits ?? 0),
    0
  )
export const activePalletLabel = (state: ReceivingFlowState) =>
  state.labels.find((label) => label.lpn === receiptProgress(state).activePallet)

export const remainingPalletContents = (state: ReceivingFlowState, lpn = receiptProgress(state).activePallet) => {
  const label = state.labels.find((entry) => entry.lpn === lpn && entry.type === 'Pallet LPN')
  if (!label || label.level === 'Full pallet') return null
  // Resolve older, retained Preview labels from their validated measurement source.
  const profile = state.measurements ? measuredProfile(state.measurements) : null
  const plan =
    label.contents ??
    (profile
      ? planPalletContents(
          label.quantity,
          state.preprocess.carton === 'confirmed' ? profile.carton.units : 0,
          state.preprocess.inner === 'confirmed' ? profile.inner.units : 0
        )
      : null)
  if (!plan) return null
  const r = receiptProgress(state)
  const children = state.labels.filter(
    (child) => r.containers[child.lpn]?.parentLpn === lpn && r.containers[child.lpn]?.status === 'received'
  )
  return {
    ...plan,
    cartons: Math.max(0, plan.cartons - children.filter((child) => child.type === 'Carton LPN').length),
    inners: Math.max(0, plan.inners - children.filter((child) => child.type === 'Inner LPN').length),
    looseEaches: Math.max(0, plan.looseEaches - (r.containers[label.lpn]?.directQuantity ?? 0)),
  }
}

export const canFinishPallet = (state: ReceivingFlowState) => {
  const r = receiptProgress(state)
  const pallet = activePalletLabel(state)
  const remaining = remainingPalletContents(state)
  return (
    !!pallet &&
    !!remaining &&
    r.containers[pallet.lpn]?.received === pallet.quantity &&
    remaining.cartons === 0 &&
    remaining.inners === 0 &&
    remaining.looseEaches === 0
  )
}

export const palletNeedsText = (state: ReceivingFlowState) => {
  const remaining = remainingPalletContents(state)
  if (!remaining) return ''
  return (
    [
      remaining.cartons
        ? `${remaining.cartons} carton${remaining.cartons === 1 ? '' : 's'} × ${remaining.cartonUnits}`
        : '',
      remaining.inners
        ? `${remaining.inners} inner pack${remaining.inners === 1 ? '' : 's'} × ${remaining.innerUnits}`
        : '',
      remaining.looseEaches ? `${remaining.looseEaches} loose eaches` : '',
    ]
      .filter(Boolean)
      .join(' · ') || 'Target reached'
  )
}
const failure = (state: ReceivingFlowState, message: string): ReceivingFlowState => ({
  ...state,
  status: { tone: 'error', message },
})
const attributeConflict = (state: ReceivingFlowState, message: string): ReceivingFlowState => ({
  ...failure(state, message),
  step: 'receipt-attribute-conflict',
})
const change = (
  state: ReceivingFlowState,
  fields: Partial<ReceiptProgress>,
  step = state.step
): ReceivingFlowState => ({
  ...state,
  step,
  receipt: { ...receiptProgress(state), ...fields },
  status: null,
})
const normalized = (value: string) => value.trim().toUpperCase()
const identity = (value: string) =>
  normalized(value)
    .replace(/^\]C1/, '')
    .replace(/^\(00\)/, '')

const canCapture = (state: ReceivingFlowState) =>
  !state.captureResult &&
  !!state.activeVariant &&
  !!state.receivingLocation &&
  !!state.labels.length &&
  state.labels.every((label) => label.printed && label.confirmed)
const validatePending = (state: ReceivingFlowState, pending: PendingReceipt): string | null => {
  if (!canCapture(state)) return 'Confirm labels and scan the receiving location first.'
  const r = receiptProgress(state)
  const label = state.labels.find((entry) => entry.lpn === pending.lpn)
  if (label?.voided) return `${pending.lpn} was voided and cannot be received.`
  if (pending.kind !== 'loose' && (!label || label.sku !== state.activeVariant?.sku))
    return 'Container does not belong to this variant.'
  if (pending.kind !== 'loose' && r.containers[pending.lpn] && r.containers[pending.lpn].status !== 'in-progress')
    return `${pending.lpn} was already received.`
  const parent = state.labels.find((entry) => entry.lpn === pending.parentLpn && entry.type === 'Pallet LPN')
  if (!parent) return 'Scan a planned pallet first.'
  if (r.containers[parent.lpn] && r.containers[parent.lpn].location !== state.receivingLocation?.barcode)
    return `Resume this pallet at ${r.containers[parent.lpn].location}.`
  if (r.containers[parent.lpn] && r.containers[parent.lpn].status !== 'in-progress')
    return `${parent.lpn} is complete and cannot be reopened.`
  if (pending.quantity > receiptRemaining(state)) return 'Quantity exceeds the remaining receiving plan.'
  if ((r.containers[parent.lpn]?.received ?? 0) + pending.quantity > parent.quantity)
    return 'Pallet target exceeded. Use another pallet.'
  if (pending.kind !== 'pallet') {
    const remaining = remainingPalletContents(state, parent.lpn)
    if (!remaining) return 'This pallet has no validated content plan.'
    if (pending.kind === 'loose' && pending.quantity > remaining.looseEaches)
      return 'Quantity exceeds planned loose eaches for this pallet.'
    if (pending.kind === 'child') {
      const carton = label?.type === 'Carton LPN'
      const count = carton ? remaining.cartons : remaining.inners
      const units = carton ? remaining.cartonUnits : remaining.innerUnits
      if (count <= 0 || pending.quantity !== units)
        return `This ${carton ? 'carton' : 'inner'} does not fit the remaining pallet plan. Scan a planned container or switch pallets.`
    }
  }
  return null
}
const commitReceipt = (
  state: ReceivingFlowState,
  pending: PendingReceipt,
  attributes?: ReceiptAttributes
): ReceivingFlowState => {
  const error = validatePending(state, pending)
  if (error) return failure(state, error)
  const r = receiptProgress(state)
  const location = state.receivingLocation!.barcode
  // Fixture clock, not a receiver-entered value or device-local date.
  attributes = { ...blankAttributes(), ...attributes, receivedDate: state.fixture.receiptDate ?? fixtureReceiptDate }
  const parent: ContainerReceipt = r.containers[pending.parentLpn] ?? {
    lpn: pending.parentLpn,
    received: 0,
    status: 'in-progress',
    location,
    directQuantity: 0,
  }
  const updatedParent: ContainerReceipt = {
    ...parent,
    received: parent.received + pending.quantity,
    status: pending.kind === 'pallet' ? 'received' : 'in-progress',
    directQuantity:
      parent.directQuantity + (pending.kind === 'loose' || pending.kind === 'pallet' ? pending.quantity : 0),
    directLot: pending.kind === 'loose' ? attributes?.lot || parent.directLot : parent.directLot,
    attributes:
      pending.kind === 'pallet' || pending.kind === 'loose' ? (attributes ?? parent.attributes) : parent.attributes,
  }
  const containers = { ...r.containers, [parent.lpn]: updatedParent }
  if (pending.kind === 'child')
    containers[pending.lpn] = {
      lpn: pending.lpn,
      received: pending.quantity,
      status: 'received',
      parentLpn: parent.lpn,
      location,
      directQuantity: pending.quantity,
      attributes,
    }
  const event: ReceiptEvent = { id: `receipt-${r.events.length + 1}`, ...pending, location, attributes }
  const next = change(
    state,
    {
      containers,
      events: [...r.events, event],
      scan: '',
      quantity: '',
      pending: null,
      replacementSubmitted: false,
      activePallet: pending.kind === 'pallet' ? null : parent.lpn,
    },
    pending.kind === 'pallet' ? 'scan-pallet-lpn' : 'build-pallet'
  )
  next.activeVariant = { ...state.activeVariant!, receivedUnits: state.activeVariant!.receivedUnits + pending.quantity }
  next.status = {
    tone: 'success',
    message: `Received ${pending.quantity} eaches${pending.kind === 'child' ? ` · ${pending.lpn}` : ''}.`,
  }
  if (pending.kind === 'pallet' && receiptRemaining(next) === 0) next.step = 'variant-complete'
  return next
}
const beginReceipt = (state: ReceivingFlowState, pending: PendingReceipt): ReceivingFlowState => {
  const error = validatePending(state, pending)
  if (error) return failure(state, error)
  if (inventoryControlFor(state.activeVariant).category !== 'received_date') {
    return change(
      state,
      { pending, attributes: blankAttributes(), replacementSubmitted: false },
      'capture-receipt-attributes'
    )
  }
  return commitReceipt(state, pending)
}

const validDate = (value: string) =>
  /^\d{4}-\d{2}-\d{2}$/.test(value) &&
  !Number.isNaN(Date.parse(value)) &&
  new Date(value).toISOString().slice(0, 10) === value
// Fixture GS1 subset: 01 GTIN, 10 lot, 11 manufacture, 15 best-before, 17 expiry.
export const parseReceiptGs1 = (scan: string): Partial<ReceiptAttributes> => {
  let raw = scan.trim().replace(/^\]C1/, '')
  const result: Partial<ReceiptAttributes> = {}
  while (raw.length) {
    if (raw[0] === '\u001d') {
      raw = raw.slice(1)
      continue
    }
    const bracket = raw.match(/^\((01|10|11|15|17)\)/)
    const ai = bracket?.[1] ?? raw.slice(0, 2)
    raw = raw.slice(bracket ? bracket[0].length : 2)
    const size =
      ai === '01'
        ? 14
        : ['11', '15', '17'].includes(ai)
          ? 6
          : ai === '10'
            ? raw.search(/[\u001d(]/) < 0
              ? raw.length
              : raw.search(/[\u001d(]/)
            : 0
    if (!size || raw.length < size)
      throw new Error('Unsupported or incomplete GS1 barcode. Enter the required attributes manually.')
    const value = raw.slice(0, size)
    raw = raw.slice(size)
    if (ai === '01') {
      if (!/^\d{14}$/.test(value)) throw new Error('GS1 GTIN must contain 14 digits.')
      result.gtin = value
    } else if (ai === '10') {
      if (value.length > 20) throw new Error('GS1 lot exceeds 20 characters.')
      result.lot = value
    } else {
      if (!/^\d{6}$/.test(value)) throw new Error('GS1 date is invalid.')
      const year = 2000 + Number(value.slice(0, 2)),
        month = Number(value.slice(2, 4))
      const day = Number(value.slice(4)) || new Date(Date.UTC(year, month, 0)).getUTCDate()
      const date = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
      if (!validDate(date)) throw new Error('GS1 date is invalid.')
      const field = ai === '11' ? 'manufactureDate' : ai === '15' ? 'bestBeforeDate' : 'expiry'
      result[field] = date
    }
  }
  if (!Object.keys(result).length) throw new Error('Scan a GS1 barcode or enter attributes manually.')
  return result
}

export const reduceReceipt = (state: ReceivingFlowState, action: ReceiptAction): ReceivingFlowState => {
  const r = receiptProgress(state)
  switch (action.type) {
    case 'set-receipt-scan':
      return change(state, { scan: action.value })
    case 'scan-receiving-pallet': {
      if (state.step !== 'scan-pallet-lpn' || !canCapture(state)) return state
      const lpn = identity(r.scan)
      if (!lpn) return failure(state, 'Scan a pallet LPN.')
      const awaitingFinish = Object.values(r.containers).find(
        (container) =>
          !container.parentLpn &&
          container.status === 'in-progress' &&
          container.received === state.labels.find((entry) => entry.lpn === container.lpn)?.quantity
      )
      if (awaitingFinish && awaitingFinish.lpn !== lpn)
        return failure(state, `Finish pallet ${awaitingFinish.lpn} before scanning another pallet.`)
      const label = state.labels.find((entry) => entry.lpn === lpn)
      if (!label) return failure(state, `LPN ${lpn} is not planned for this variant.`)
      if (label.voided) return failure(state, `${lpn} was voided and cannot be received.`)
      if (label.type !== 'Pallet LPN') return failure(state, 'Scan a pallet first, then its child containers.')
      if (r.containers[lpn]?.status === 'received')
        return failure(state, `${lpn} was already received. Scan another pallet.`)
      if (label.level === 'Full pallet')
        return beginReceipt(state, {
          kind: 'pallet',
          lpn,
          parentLpn: lpn,
          quantity: label.quantity,
          returnStep: 'scan-pallet-lpn',
        })
      const existing = r.containers[lpn]
      if (existing && existing.location !== state.receivingLocation!.barcode)
        return failure(state, `Resume this pallet at ${existing.location}.`)
      return change(
        state,
        {
          scan: '',
          activePallet: lpn,
          containers: {
            ...r.containers,
            [lpn]: existing ?? {
              lpn,
              received: 0,
              status: 'in-progress',
              location: state.receivingLocation!.barcode,
              directQuantity: 0,
            },
          },
        },
        'build-pallet'
      )
    }
    case 'scan-receiving-child': {
      if (state.step !== 'build-pallet' || !r.activePallet) return state
      const lpn = identity(r.scan)
      const label = state.labels.find((entry) => entry.lpn === lpn)
      if (!label || label.type === 'Pallet LPN')
        return failure(
          state,
          lpn ? `${lpn} is not a planned carton or inner for this variant.` : 'Scan a carton or inner LPN.'
        )
      return beginReceipt(state, {
        kind: 'child',
        lpn,
        parentLpn: r.activePallet,
        quantity: label.quantity,
        returnStep: 'build-pallet',
      })
    }
    case 'open-loose-receipt':
      if (state.step !== 'build-pallet') return state
      if (!(remainingPalletContents(state)?.looseEaches ?? 0))
        return failure(state, 'No loose eaches remain on this pallet.')
      return change(state, { quantity: '' }, 'receive-loose-eaches')
    case 'set-loose-quantity':
      return change(state, { quantity: action.value })
    case 'submit-loose-receipt': {
      if (state.step !== 'receive-loose-eaches' || !r.activePallet) return state
      const quantity = Number(r.quantity)
      if (!r.quantity.trim() || !Number.isSafeInteger(quantity) || quantity <= 0)
        return failure(state, 'Enter a positive whole number of eaches.')
      const looseReceived = r.events
        .filter((entry) => entry.kind === 'loose' && state.preprocess.reservedLpns.includes(entry.parentLpn))
        .reduce((sum, entry) => sum + entry.quantity, 0)
      if (quantity > (state.preprocess.labelPlan?.remainderEaches ?? 0) - looseReceived)
        return failure(
          state,
          'Quantity exceeds planned loose remainder eaches. Scan labelled cartons/inners for packaged units.'
        )
      return beginReceipt(state, {
        kind: 'loose',
        lpn: r.activePallet,
        parentLpn: r.activePallet,
        quantity,
        returnStep: 'receive-loose-eaches',
      })
    }
    case 'switch-receiving-pallet':
      if (state.step !== 'build-pallet') return state
      if (canFinishPallet(state)) return failure(state, 'Finish this pallet before switching.')
      return change(state, { activePallet: null, scan: '', pending: null }, 'scan-pallet-lpn')
    case 'finish-receiving-pallet': {
      if (state.step !== 'build-pallet' || !r.activePallet) return state
      const target = activePalletLabel(state)?.quantity ?? 0
      const parent = r.containers[r.activePallet]
      if (!parent || parent.received !== target)
        return failure(state, `${target - (parent?.received ?? 0)} eaches still required on this pallet.`)
      if (!canFinishPallet(state)) return failure(state, 'Pallet contents do not match the planned pack mix.')
      return change(
        state,
        {
          activePallet: null,
          scan: '',
          containers: { ...r.containers, [parent.lpn]: { ...parent, status: 'received' } },
        },
        receiptRemaining(state) === 0 ? 'variant-complete' : 'scan-pallet-lpn'
      )
    }
    case 'done-pallet-work':
      return ['scan-pallet-lpn', 'variant-complete'].includes(state.step)
        ? change(state, { scan: '' }, 'variant-menu')
        : state
    case 'set-controlled-attribute':
      return change(state, { attributes: { ...r.attributes, [action.field]: action.value } })
    case 'submit-controlled-attributes': {
      if (state.step !== 'capture-receipt-attributes' || !r.pending) return state
      const control = inventoryControlFor(state.activeVariant)
      const value = (r.attributes[control.field] ?? '').trim()
      if (/^(?:\]C1|\((?:01|10|11|15|17)\)|01\d{14})/.test(value)) {
        try {
          const parsed = parseReceiptGs1(value)
          if (!parsed[control.field])
            return failure(state, `Barcode has no ${control.label.toLowerCase()}. Enter it manually.`)
          const populated = change(state, { attributes: { ...r.attributes, ...parsed } })
          if (parsed.gtin && parsed.gtin !== state.activeVariant?.barcode)
            return attributeConflict(
              populated,
              'Scanned GTIN does not match this product. Scan the correct label or cancel this item.'
            )
          return { ...populated, status: { tone: 'info', message: `${control.label} populated. Continue to confirm.` } }
        } catch (error) {
          return failure(state, error instanceof Error ? error.message : 'Invalid barcode.')
        }
      }
      const attributes = { ...r.attributes, lot: r.attributes.lot.trim(), gtin: r.attributes.gtin.trim() }
      attributes[control.field] = value
      if (!value) return failure(state, `Enter the required ${control.label.toLowerCase()}.`)
      if (control.date && !validDate(value))
        return failure(state, `Enter a valid ${control.label.toLowerCase()} (YYYY-MM-DD).`)
      if (attributes.gtin && attributes.gtin !== state.activeVariant?.barcode)
        return attributeConflict(
          state,
          'Scanned GTIN does not match this product. Scan the correct label or cancel this item.'
        )
      const label = state.labels.find((entry) => entry.lpn === r.pending!.lpn)
      const expected = r.pending.kind === 'loose' ? r.containers[r.pending.parentLpn]?.attributes : label?.attributes
      const fields = [
        inventoryControls.lot_number,
        inventoryControls.expiry_date,
        inventoryControls.manufacture_date,
        inventoryControls.best_before_date,
      ]
      for (const field of fields) {
        const recorded = expected?.[field.field]
        if (recorded && attributes[field.field] && recorded !== attributes[field.field])
          return attributeConflict(
            state,
            `${field.label} conflicts with this container. Label: ${recorded}. Entered: ${attributes[field.field]}.`
          )
        if (recorded && !attributes[field.field]) attributes[field.field] = recorded
      }
      const next = change(state, { attributes })
      if (action.captureOnly)
        return { ...next, status: { tone: 'info', message: `${control.label} captured. Continue to confirm.` } }
      const replace =
        r.pending.kind !== 'loose' &&
        fields.some(({ field }) => attributes[field] && label?.attributes?.[field] !== attributes[field])
      return replace ? { ...next, step: 'replace-container-label' } : commitReceipt(next, r.pending, attributes)
    }
    case 'cancel-pending-receipt': {
      if (!r.pending || !['capture-receipt-attributes', 'receipt-attribute-conflict'].includes(state.step)) return state
      return {
        ...change(
          state,
          {
            pending: null,
            scan: '',
            quantity: '',
            attributes: blankAttributes(),
          },
          r.pending.kind === 'pallet' ? 'scan-pallet-lpn' : 'build-pallet'
        ),
        status: { tone: 'info', message: 'Item cancelled. No quantity received.' },
      }
    }
    case 'print-container-replacement': {
      if (state.step !== 'replace-container-label' || !r.pending || r.replacementSubmitted) return state
      if (!receivingPrinters.some((printer) => printer.id === state.printerId))
        return failure(state, 'No valid label printer configured. Check RF settings.')
      const status = state.nextPrintOutcome ?? 'accepted'
      const next = change(state, {
        replacementSubmitted: status === 'accepted',
        replacementRequests: [
          ...r.replacementRequests,
          {
            lpn: r.pending.lpn,
            printerId: state.printerId,
            status,
            confirmed: false,
            sku: state.activeVariant?.sku ?? '',
            quantity: r.pending.quantity,
            attributes: { ...r.attributes },
          },
        ],
      })
      next.nextPrintOutcome = 'accepted'
      return status === 'accepted'
        ? next
        : failure(next, 'Replacement print request was not accepted. Retry the same identity.')
    }
    case 'decline-container-replacement':
      return change(state, {
        replacementSubmitted: false,
        replacementRequests: r.replacementRequests.map((entry, index) =>
          index === r.replacementRequests.length - 1 && r.replacementSubmitted ? { ...entry, declined: true } : entry
        ),
      })
    case 'confirm-container-replacement': {
      if (state.step !== 'replace-container-label' || !r.pending || !r.replacementSubmitted) return state
      const error = validatePending(state, r.pending)
      if (error) return failure(state, error)
      const next = {
        ...change(state, {
          replacementRequests: r.replacementRequests.map((entry, index) =>
            index === r.replacementRequests.length - 1 ? { ...entry, confirmed: true } : entry
          ),
        }),
        labels: state.labels.map((label) =>
          label.lpn === r.pending!.lpn
            ? {
                ...label,
                attributes: {
                  lot: r.attributes.lot,
                  expiry: r.attributes.expiry,
                  manufactureDate: r.attributes.manufactureDate,
                  bestBeforeDate: r.attributes.bestBeforeDate,
                },
              }
            : label
        ),
      }
      return commitReceipt(next, r.pending, r.attributes)
    }
    case 'receipt-back':
      if (r.replacementSubmitted) return reduceReceipt(state, { type: 'decline-container-replacement' })
      if (state.step === 'receipt-attribute-conflict') return change(state, {}, 'capture-receipt-attributes')
      if (state.step === 'replace-container-label')
        return change(state, { replacementSubmitted: false }, 'capture-receipt-attributes')
      if (state.step === 'capture-receipt-attributes' && r.pending)
        return change(state, { pending: null }, r.pending.returnStep)
      if (state.step === 'receive-loose-eaches') return change(state, {}, 'build-pallet')
      if (state.step === 'build-pallet') return change(state, { activePallet: null, scan: '' }, 'scan-pallet-lpn')
      if (state.step === 'variant-complete') return change(state, {}, 'variant-menu')
      return state
    case 'set-fixture-inventory-category':
      if (!state.activeVariant || r.events.length || r.pending) return state
      return {
        ...state,
        activeVariant: { ...state.activeVariant, inventoryControlCategory: action.category },
      }
  }
}
